import { useState, type ReactElement } from 'react'
import { router } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import { PageContainer } from '@/components/PageContainer'
import { ErrorBanner } from '@/components/ErrorBanner'
import { Button } from '@/components/ui/button'
import { usePollUntil } from '@/hooks/usePolling'
import type { ProjectNav } from '@/types/project'
import type { RequirementsSpecificationSummary, PipelineStepData } from '@/types/requirements_specification'
import { PhaseRow } from './components/PhaseRow'

interface Props {
  project_nav: ProjectNav
  requirements_specification_summary: RequirementsSpecificationSummary
  failed: boolean
  pipeline_steps?: Record<string, PipelineStepData>
  source_note_id: number | null
}

const PIPELINE_PHASES = [
  {
    key: 'concept_model_extraction',
    label: '概念モデル抽出',
    desc: 'Resource / Event の識別・命名辞書の確立'
  },
  {
    key: 'domain_verification',
    label: 'ドメイン検証',
    desc: '概念モデルの根拠検証'
  },
  {
    key: 'business_flow_composition',
    label: '業務フロー構成',
    desc: 'Event を時系列化し業務フローを構成'
  },
  {
    key: 'demand_extraction',
    label: '要求抽出',
    desc: '要求を抽出し用語ズレを検出'
  },
  {
    key: 'requirement_extraction',
    label: '要件抽出',
    desc: '要件抽出・要求への紐付け'
  },
  {
    key: 'feature_list_with_crud',
    label: '機能一覧・CRUD導出',
    desc: '整合性・状態遷移・権限マトリクスの構築'
  },
  {
    key: 'validation',
    label: '検証',
    desc: '漏れの追加検証'
  }
]

export default function RequirementsSpecificationsProcessing ({
  project_nav: project,
  requirements_specification_summary: rd,
  failed = false,
  pipeline_steps: pipelineSteps = {},
  source_note_id: sourceNoteId = null
}: Props): ReactElement {
  const [pollTimedOut, setPollTimedOut] = useState(false)
  usePollUntil(!failed, 5000, { maxAttempts: 150, onError: () => setPollTimedOut(true) })

  function handleRetry (): void {
    if (sourceNoteId == null) return
    router.post(`/projects/${project.id}/source_notes/${sourceNoteId}/retry_analyze`)
  }

  return (
    <AppLayout project={project}>
      <PageContainer maxWidth='640px'>
        {pollTimedOut && (
          <ErrorBanner message='処理に時間がかかっています。ページを再読み込みしてください。' />
        )}
        {failed && (
          <ErrorBanner message='解析中にエラーが発生しました。再試行してください。' />
        )}

        <div style={{ marginBottom: 20 }}>
          <div
            style={{
              fontSize: 22,
              fontWeight: 700,
              letterSpacing: '-.04em',
              color: 'var(--bp-ink)',
              marginBottom: 6
            }}
          >
            要件を解析しています
          </div>
          <div style={{ fontSize: 14, color: 'var(--bp-ink3)', lineHeight: 1.6 }}>
            7フェーズのパイプラインで概念モデル・要求・要件・業務フローを構築中です。<br />
            完了までしばらくお待ちください。
          </div>
        </div>

        <div
          className='bg-(--bp-surface) border border-(--bp-border) rounded-[10px]' style={{
            marginBottom: 20,
            overflow: 'hidden'
          }}
        >
          {PIPELINE_PHASES.map((phase, i) => {
            const step = pipelineSteps[phase.key]
            const status = step?.status ?? 'pending'
            return <PhaseRow key={phase.key} phase={phase} num={i + 1} status={status} />
          })}
        </div>

        <div
          style={{
            background: '#0E1624',
            borderRadius: 8,
            padding: '14px 16px',
            fontFamily: 'var(--bp-font-mono)',
            fontSize: 11,
            lineHeight: 1.9,
            color: '#8DA8C4',
            maxHeight: 150,
            overflowY: 'auto'
          }}
        >
          {PIPELINE_PHASES.map((phase) => {
            const step = pipelineSteps[phase.key]
            if (step == null || step.status === 'pending') return null
            return (
              <div key={phase.key}>
                [{step.status === 'completed' ? '✓' : '▶'}] {phase.label}
                {step.elapsed_seconds != null && step.status === 'completed'
                  ? ` (${step.elapsed_seconds.toFixed(1)}s)`
                  : ''}
              </div>
            )
          })}
          {!failed && <div style={{ animation: 'srpulse 1.4s ease-in-out infinite' }}>処理中...</div>}
        </div>

        {failed && (
          <div style={{ marginTop: 20 }}>
            <div style={{ marginTop: 12 }}>
              <Button onClick={handleRetry}>
                再解析する
              </Button>
            </div>
          </div>
        )}
      </PageContainer>
    </AppLayout>
  )
}
